import React, { memo, useMemo } from 'react';
import moment from 'moment';
import { type GridRenderCellParams, type GridValidRowModel } from '@mui/x-data-grid-pro';
import { CustomTooltipWithLabel, TableLayout } from '../../../../globalUtils/globalExports';
import ExecutionStatusChip from './ExecutionStatusChip';
import { CHIP_VARIANT_BY_RESULT, COMMAND_RESULT_BY_STATUS } from './executionLogs.config';
import { type CommandResult, type IRemoteCommandExecutionUnit } from './RemoteCommandExecutionLogs';

interface ExecutionUnitsTableProps {
  tableId: string;
  unitName: string;
  units: IRemoteCommandExecutionUnit[];
}

const ROW_HEIGHT = 40;
const HEADER_HEIGHT = 44;
const TIME_FORMAT = 'HH:mm:ss';

function ExecutionUnitsTable({ tableId, unitName, units }: ExecutionUnitsTableProps) {
  const columns = useMemo(() => {
    return [
      {
        field: 'deviceType',
        headerName: 'Device Type',
        minWidth: 100,
        editable: false,
        sortable: false,
        flex: 0.8,
      },
      {
        field: 'unit',
        headerName: 'Unit',
        minWidth: 90,
        editable: false,
        sortable: false,
        flex: 0.7,
      },
      {
        field: 'startedOn',
        headerName: 'Started On',
        minWidth: 90,
        editable: false,
        sortable: false,
        flex: 0.7,
      },
      {
        field: 'completedOn',
        headerName: 'Completed On',
        minWidth: 100,
        editable: false,
        sortable: false,
        flex: 0.7,
      },
      {
        field: 'result',
        headerName: 'Execution Status',
        minWidth: 130,
        editable: false,
        sortable: false,
        flex: 1,
        renderCell: (params: GridRenderCellParams<GridValidRowModel, CommandResult>): JSX.Element => {
          return <ExecutionStatusChip label={params?.row?.result} variant={CHIP_VARIANT_BY_RESULT[params?.row?.result]} />;
        },
      },
      {
        field: 'failureReason',
        headerName: 'Failure Reason',
        minWidth: 140,
        editable: false,
        sortable: false,
        flex: 1.2,
        renderCell: (params: GridRenderCellParams<GridValidRowModel, string>): JSX.Element => {
          return (
            <CustomTooltipWithLabel
              label={params?.row?.failureReason}
              labelId={'remote-command-unit-failure-' + params?.row?.id}
              containerClassName={params?.row?.result === 'Failed' ? 'remote__command__logs-failure-text' : ''}
            />
          );
        },
      },
    ];
  }, []);

  const rows = useMemo(() => {
    return units?.map((unit) => {
      return {
        id: unit?.id,
        deviceType: unit?.deviceType ?? '—',
        unit: unitName + ' ' + unit?.unitNumber,
        startedOn: unit?.startedAt ? moment(unit?.startedAt).format(TIME_FORMAT) : '—',
        completedOn: unit?.completedAt ? moment(unit?.completedAt).format(TIME_FORMAT) : '—',
        result: COMMAND_RESULT_BY_STATUS[unit?.commandStatus] ?? 'Pending',
        failureReason: unit?.failureReason ?? '',
      };
    }) ?? [];
  }, [units, unitName]);

  return (
    <div className='remote__command__logs-table-wrap' style={{ height: `${HEADER_HEIGHT + rows?.length * ROW_HEIGHT + 2}px` }}>
      <TableLayout
        tableId={tableId}
        tableClassName='remote__command__logs-table'
        gridColumns={columns}
        tableRows={rows}
        layoutView='list'
        totalCount={rows?.length}
        tablePageSize={Math.max(rows?.length, 1)}
        disablePaginationFooter={true}
        handleCallBackFromParent={true}
        showSkeleton={false}
        showLoader={false}
      />
    </div>
  );
}

export default memo(ExecutionUnitsTable);
